import * as React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, AlertTitle, Box, Button } from '@mui/material';

import styles from './ParamsPreview.module.css';

export interface ParamsIssue {
  path: string;
  message: string;
}

export interface ParamsPreviewProps {
  params: unknown;
  issues: ParamsIssue[];
}

/** Shows the exact JSON that will be submitted and the result of schema validation. */
const ParamsPreview: React.FC<ParamsPreviewProps> = ({ params, issues }) => {
  const [copied, setCopied] = React.useState(false);
  const json = React.useMemo(() => JSON.stringify(params, null, 2), [params]);

  React.useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box className={styles.preview}>
      {issues.length === 0 ? (
        <Alert severity="success">The report parameters satisfy the report schema.</Alert>
      ) : (
        <Alert severity="error">
          <AlertTitle>
            {issues.length} schema issue{issues.length === 1 ? '' : 's'} must be fixed before submitting
          </AlertTitle>
          <ul className={styles.issues}>
            {issues.map((issue) => (
              <li key={`${issue.path}:${issue.message}`}>
                <code>{issue.path || '(root)'}</code> {issue.message}
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <Box className={styles.toolbar}>
        <Button
          size="small"
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={copy}
        >
          {copied ? 'Copied' : 'Copy JSON'}
        </Button>
      </Box>
      <pre className={styles.json} aria-label="Report parameters JSON">
        {json}
      </pre>
    </Box>
  );
};

export default ParamsPreview;
